import { createBimSceneFileSignedDownloadUrl } from "apis/procore/bimSceneFileSignedDownloadUrl";
import { log } from "utils/log";

import type { DrawingData } from "../types";
import { readMinimapFileOffline } from "./offlineStore";
import {
    buildTileSetFileUrl,
    type ParsedManifestUrl,
    resolveTiledManifestUrl,
    TILE_MANIFEST_RELATIVE_PATH,
} from "./tiledSource";

const l = log.miniMap.tiled;

/** Model Manager's import-status value once a file's tile set is actually ready. */
const COMPLETED_STATUS = "COMPLETED";

/** True once both of Model Manager's per-file import-status fields report the tile set is ready. */
function isTileSetProcessingComplete(
    drawingData: Pick<DrawingData, "platformFileStatus" | "sceneVersionStatus">,
): boolean {
    return drawingData.platformFileStatus === COMPLETED_STATUS && drawingData.sceneVersionStatus === COMPLETED_STATUS;
}

/**
 * Inputs for the tiled route of one drawing (RFD00143 option C). Presence
 * means the host rollout flag is ON; `undefined` keeps the drawing on the
 * pure PDF route.
 */
export type TilingRequest = {
    companyId: number;
    projectId: number;
    sceneId: string;
    /** Scene version the sheets were resolved from (`current` when unknown). */
    sceneVersionId: string;
    /** Scene-file placement id (`platform_file_instance_id` from plans/extended). */
    platformFileInstanceId?: string;
    /**
     * True when this drawing is destined for the tiled route (has a
     * `platformFileInstanceId`) but Model Manager hasn't finished importing
     * it yet, AND the caller is on the Procore mobile design. `downloadDrawingData`
     * neither attempts the tiled route nor falls back to PDF while this is
     * true: falling back to PDF is exactly the pdf.js memory/crash risk the
     * tiled route exists to avoid on mobile (BM-13878), so a not-yet-ready
     * tile set must not be worse than simply staying `Loading` until the next
     * refetch (e.g. a page reload) rechecks it. Web ignores this — it has no
     * PDF-route crash risk, so falling back immediately is fine there.
     */
    waitForMobileProcessing?: boolean;
};

/**
 * Bundle the tiled-route inputs for one drawing, or `undefined` when the
 * route is disabled or the Procore scene context is incomplete.
 */
export function buildTilingRequest(
    enabled: boolean,
    procoreContext: { companyId: number; projectId: number; sceneVersionId?: string } | null | undefined,
    sceneId: string | null | undefined,
    drawingData: Pick<DrawingData, "platformFileInstanceId" | "platformFileStatus" | "sceneVersionStatus">,
    isMobileDesign: boolean,
): TilingRequest | undefined {
    if (!enabled || !procoreContext || !sceneId) {
        return undefined;
    }
    return {
        companyId: procoreContext.companyId,
        projectId: procoreContext.projectId,
        sceneId,
        sceneVersionId: procoreContext.sceneVersionId ?? "current",
        platformFileInstanceId: drawingData.platformFileInstanceId,
        waitForMobileProcessing:
            isMobileDesign && Boolean(drawingData.platformFileInstanceId) && !isTileSetProcessingComplete(drawingData),
    };
}

/**
 * Mint (or re-mint) a live tile set root from Platform's signed-download-URL
 * endpoint. `null` when the endpoint is unreachable or returns nothing
 * usable (e.g. no import yet).
 *
 * Deduped and cached by `createBimSceneFileSignedDownloadUrl` itself, keyed
 * by placement id and reused until near expiry -- so it costs one network
 * round trip the first time it's called for a given signature window,
 * whether that call happens here (no cached manifest yet) or later, lazily,
 * from `fetchTileSetFile` on a tile's first OPFS miss. There is no reason to
 * pay it eagerly just because a manifest happened to already be cached.
 */
function resolveLiveBaseUrl(request: TilingRequest): Promise<string | null> {
    return createBimSceneFileSignedDownloadUrl({
        companyId: request.companyId,
        projectId: request.projectId,
        sceneId: request.sceneId,
        sceneVersionId: request.sceneVersionId,
        fileInstanceId: request.platformFileInstanceId!,
    });
}

/**
 * Locate a drawing's tile set manifest, or `null` when it has none (→ PDF
 * route). Tries the dev direct-manifest URL / localStorage override first.
 *
 * Then, whenever this drawing has a `platformFileInstanceId`, probes OPFS for
 * an already-cached manifest (RFD00000145): a tile set's identity is treated
 * as immutable once resolved (see `minimapFilePath`'s doc), so a cached
 * manifest doesn't need to be re-proven against the network before use. A
 * hit resolves an offline-only ref immediately -- no network call of any
 * kind, not even in the background -- but still carries a `refreshBaseUrl`
 * resolver, so `fetchTileSetFile` can mint a live prefix lazily, only if and
 * when a tile actually misses OPFS (a pan or zoom into something this
 * device hasn't cached yet). A failure there just fails that one tile
 * fetch; it doesn't affect the manifest, which is already in hand.
 *
 * A cache miss falls through to the RFD00143 option C flow (scene-file
 * placement id → Platform signed-download-URL endpoint →
 * `tile/manifest_1.json` under the returned prefix), blocking this time:
 * with nothing cached, availability must be proven by actually reaching the
 * endpoint. Any failure -- offline, or the endpoint unreachable/empty --
 * returns `null`, same as PDF in `downloadDrawingData`.
 */
export async function resolveTiledManifest(
    drawingUrl: string,
    request: TilingRequest,
): Promise<ParsedManifestUrl | null> {
    const direct = resolveTiledManifestUrl(drawingUrl);
    if (direct) {
        return direct;
    }

    if (!request.platformFileInstanceId) {
        return null;
    }

    const cachedManifest = await readMinimapFileOffline(request.platformFileInstanceId, TILE_MANIFEST_RELATIVE_PATH);
    if (cachedManifest) {
        l.debug("Resolved tile set from OPFS:", drawingUrl);
        return {
            platformFileInstanceId: request.platformFileInstanceId,
            refreshBaseUrl: () => resolveLiveBaseUrl(request),
        };
    }

    if (navigator.onLine) {
        const baseUrl = await resolveLiveBaseUrl(request);
        if (baseUrl) {
            const manifestUrl = buildTileSetFileUrl(baseUrl, TILE_MANIFEST_RELATIVE_PATH);
            l.debug("Resolved tile set via signed download URL:", drawingUrl, manifestUrl);
            return {
                baseUrl,
                manifestUrl,
                platformFileInstanceId: request.platformFileInstanceId,
                refreshBaseUrl: () => resolveLiveBaseUrl(request),
            };
        }
    }

    return null;
}
